import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { Admin } from './pages/Admin';
import { ApprenticesList } from './pages/ApprenticesList';
import { AddApprentice } from './pages/AddApprentice';
import { ApprenticeDetail } from './pages/ApprenticeDetail';
import { TrackingPage } from './pages/TrackingPage';
import { LogOTJ } from './pages/LogOTJ';
import { MyOTJHours } from './pages/MyOTJHours';
import { StandardsList } from './pages/StandardsList';
import ILRGeneration from './pages/ILRGeneration';
import { InstructorsList } from './pages/InstructorsList';
import { CoursesList } from './pages/CoursesList';
import { ProgressReviewsList } from './pages/ProgressReviewsList';
import { CreateProgressReview } from './pages/CreateProgressReview';
import { ProgressReviewDetail } from './pages/ProgressReviewDetail';
import { RoleBasedRoute } from './components/RoleBasedRoute';

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Layout />}>
            {/* Dashboard - All authenticated users */}
            <Route index element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <Dashboard />
              </RoleBasedRoute>
            } />

            {/* Admin - Administrators only */}
            <Route path="admin" element={
              <RoleBasedRoute allowedRoles={['administrator']}>
                <Admin />
              </RoleBasedRoute>
            } />

            {/* Instructors - Administrators only */}
            <Route path="instructors" element={
              <RoleBasedRoute allowedRoles={['administrator']}>
                <InstructorsList />
              </RoleBasedRoute>
            } />

            {/* Courses - Administrators only */}
            <Route path="courses" element={
              <RoleBasedRoute allowedRoles={['administrator']}>
                <CoursesList />
              </RoleBasedRoute>
            } />

            {/* Progress Reviews - Instructors and Administrators */}
            <Route path="progress-reviews" element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <ProgressReviewsList />
              </RoleBasedRoute>
            } />

            <Route path="progress-reviews/new" element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <CreateProgressReview />
              </RoleBasedRoute>
            } />

            <Route path="progress-reviews/:id" element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <ProgressReviewDetail />
              </RoleBasedRoute>
            } />

            {/* Apprentices - Instructors and Administrators */}
            <Route path="apprentices" element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <ApprenticesList />
              </RoleBasedRoute>
            } />

            <Route path="apprentices/new" element={
              <RoleBasedRoute allowedRoles={['administrator']}>
                <AddApprentice />
              </RoleBasedRoute>
            } />

            <Route path="apprentices/:id" element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <ApprenticeDetail />
              </RoleBasedRoute>
            } />

            {/* Tracking - Instructors and Administrators */}
            <Route path="tracking" element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <TrackingPage />
              </RoleBasedRoute>
            } />

            {/* Log OTJ - All authenticated users */}
            <Route path="tracking/otj/new" element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <LogOTJ />
              </RoleBasedRoute>
            } />

            {/* My OTJ Hours - Learners only */}
            <Route path="my-otj" element={
              <RoleBasedRoute allowedRoles={['learner']}>
                <MyOTJHours />
              </RoleBasedRoute>
            } />

            {/* Standards - All authenticated users */}
            <Route path="standards" element={
              <RoleBasedRoute allowedRoles={['learner', 'instructor', 'administrator']}>
                <StandardsList />
              </RoleBasedRoute>
            } />

            {/* ILR - Administrators only */}
            <Route path="ilr" element={
              <RoleBasedRoute allowedRoles={['administrator']}>
                <ILRGeneration />
              </RoleBasedRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;
